import React, { useEffect, useState } from 'react';
import api from '../lib/api';

/**
 * AppUpdateBanner — Phase 76i.
 *
 * Detects outdated installs of the Carloo Android shell and prompts the user
 * to install the latest APK in one tap. Android does NOT permit silent OTA
 * updates outside the Play Store, so the closest to "auto-update" we can
 * get is:
 *   1. Detect installed versionCode via @capacitor/app
 *   2. Query backend for the latest release (/api/app/latest-release)
 *   3. If installed < latest: show a non-dismissable amber banner
 *   4. On tap, navigate to the APK URL — Android's native package installer
 *      takes over and the user taps "Install" once
 *
 * For PWA / browser users, the page reload auto-loads new JS so they don't
 * need this banner.
 *
 * For one-time migration from the old debug-signed v2.0 to release-signed
 * v2.0.1+, Android will reject the in-place update because keystores
 * differ; users will get "App not installed" and must uninstall first.
 * From v2.0.1 onward all updates use /app/keystores/carloo-release.jks so
 * future updates install seamlessly.
 */
export default function AppUpdateBanner() {
  const [info, setInfo] = useState(null);
  const [installed, setInstalled] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [snoozed, setSnoozed] = useState(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Phase 78ad — Dismiss & snooze:
  // 1. If user taps "Update Now" the banner is immediately hidden so it
  //    doesn't bounce back during the OS install flow. We persist that
  //    against the latest version_code so once a NEWER version ships
  //    the banner returns automatically.
  // 2. A dedicated "Later" button puts the banner to sleep for 24h.
  // 3. Auto-clears when the installed code catches up.
  const SNOOZE_KEY = 'carloo_apk_snooze_v2';

  const isSnoozed = (latestCode) => {
    try {
      const raw = localStorage.getItem(SNOOZE_KEY);
      if (!raw) return false;
      const obj = JSON.parse(raw);
      if (!obj || obj.code !== latestCode) return false;
      if (obj.until && Date.now() < obj.until) return true;
    } catch {}
    return false;
  };

  const persistSnooze = (latestCode, hours = 24) => {
    try {
      localStorage.setItem(SNOOZE_KEY, JSON.stringify({
        code: latestCode,
        until: Date.now() + hours * 60 * 60 * 1000,
      }));
    } catch {}
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only run inside the Carloo native shell. Pure PWA users don't have
      // an APK to update; their JS auto-refreshes on reload.
      const isCarlooApp = typeof navigator !== 'undefined' && /CarlooApp\//.test(navigator.userAgent || '');
      if (!isCarlooApp) return;
      try {
        const { App: CapApp } = await import('@capacitor/app');
        const i = await CapApp.getInfo();
        if (!cancelled) setInstalled(i);
      } catch {
        return;
      }
      try {
        const { data } = await api.get('/app/latest-release');
        if (!cancelled) setInfo(data);
        if (!cancelled && data?.version_code) {
          setSnoozed(isSnoozed(parseInt(data.version_code, 10) || 0));
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  if (!installed || !info?.available) return null;
  let installedCode = parseInt(installed.build || '0', 10);
  if (!Number.isFinite(installedCode)) installedCode = 0;
  const latestCode = parseInt(info.version_code || '0', 10) || 0;
  if (installedCode >= latestCode) return null;
  if (snoozed) return null;

  const startUpdate = () => {
    setDownloading(true);
    const url = info.download_url
      ? (info.download_url.startsWith('http') ? info.download_url : `${API_BASE}${info.download_url}`)
      : `${API_BASE}/api/uploads/vehicle.apk`;
    window.location.href = url;
    // Hide banner while Android handles the install. If the install
    // succeeds, on next launch `installedCode >= latestCode` and the
    // banner stays hidden permanently. If it fails or is cancelled, the
    // 24h snooze keeps things quiet but the user can retry from the
    // "App Update" tile in the bottom nav.
    persistSnooze(latestCode, 24);
    setSnoozed(true);
    setTimeout(() => setDownloading(false), 30000);
  };

  const snoozeLater = () => {
    persistSnooze(latestCode, 24);
    setSnoozed(true);
  };

  return (
    <div
      data-testid="app-update-banner"
      className="fixed top-0 left-0 right-0 z-[10000] bg-[#FF8C00] text-white shadow-lg"
      style={{ paddingTop: 'env(safe-area-inset-top, 0)' }}
    >
      <div className="px-3 py-2 flex items-center justify-between gap-2 max-w-screen-md mx-auto">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-widest font-bold leading-tight">
            New Version Available
          </p>
          <p className="text-xs leading-tight mt-0.5 truncate">
            v{info.version_name} (build #{latestCode}). You're on v{installed.version || '?'} (build #{installedCode}).
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={snoozeLater}
            className="text-white/80 hover:text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1.5"
            data-testid="app-update-later-btn"
            title="Hide for 24 hours"
          >
            Later
          </button>
          <button
            type="button"
            onClick={startUpdate}
            disabled={downloading}
            className="bg-white text-[#FF8C00] font-bold uppercase tracking-widest text-xs px-3 py-1.5 rounded-full whitespace-nowrap hover:bg-[#0A0A0A] hover:text-white disabled:opacity-60 transition-colors"
            data-testid="app-update-install-btn"
          >
            {downloading ? 'Opening…' : 'Update Now'}
          </button>
        </div>
      </div>
    </div>
  );
}
